import React from 'react';

// Fireflies - sophisticated golden glowing dots
export const Fireflies = ({ count = 40 }) => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          className="firefly"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${Math.random() * 4 + 6}s`,
          }}
        />
      ))}
    </div>
  );
};

// Elegant moth flying across (replaces butterfly)
export const Moth = ({ delay = 0, top = '20%' }) => {
  return (
    <div
      className="moth absolute pointer-events-none z-10"
      style={{ top, left: '-60px', animationDelay: `${delay}s` }}
    >
      <svg width="50" height="50" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <g className="moth-wings">
          <path d="M50 50 Q 25 30, 15 50 Q 20 70, 50 60 Z" fill="url(#moth-wing)" opacity="0.85" />
          <path d="M50 50 Q 75 30, 85 50 Q 80 70, 50 60 Z" fill="url(#moth-wing)" opacity="0.85" />
          <path d="M50 50 Q 30 70, 25 85 Q 40 80, 50 70 Z" fill="url(#moth-wing-2)" opacity="0.7" />
          <path d="M50 50 Q 70 70, 75 85 Q 60 80, 50 70 Z" fill="url(#moth-wing-2)" opacity="0.7" />
          <ellipse cx="50" cy="55" rx="2.5" ry="18" fill="#1f2937" />
          <circle cx="50" cy="38" r="3" fill="#1f2937" />
        </g>
        <defs>
          <linearGradient id="moth-wing" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#065f46" />
            <stop offset="100%" stopColor="#d4af37" />
          </linearGradient>
          <linearGradient id="moth-wing-2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#064e3b" />
            <stop offset="100%" stopColor="#92400e" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

// Elegant Fern frond - natural forest element
export const Fern = ({ position = {}, rotation = 0, size = 120, opacity = 0.5 }) => {
  return (
    <div
      className="absolute pointer-events-none fern-sway"
      style={{ ...position, transform: `rotate(${rotation}deg)`, width: size, height: size, opacity }}
    >
      <svg width={size} height={size} viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="fern-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#064e3b" />
            <stop offset="50%" stopColor="#065f46" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        {/* Main stem */}
        <path d="M 100 180 Q 100 100 100 20" stroke="url(#fern-gradient)" strokeWidth="3" fill="none" />
        {/* Leaflets - pairs going up the stem */}
        {[...Array(8)].map((_, i) => {
          const y = 30 + i * 18;
          const length = 50 - i * 4;
          return (
            <g key={i}>
              <ellipse cx={100 - length / 2 - 5} cy={y} rx={length / 2} ry={6} fill="url(#fern-gradient)" transform={`rotate(${-30 - i * 2} ${100 - length / 2 - 5} ${y})`} />
              <ellipse cx={100 + length / 2 + 5} cy={y} rx={length / 2} ry={6} fill="url(#fern-gradient)" transform={`rotate(${30 + i * 2} ${100 + length / 2 + 5} ${y})`} />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Glowing mystical crystal (replaces mushroom)
export const GlowingCrystal = ({ position = {}, size = 60, color = 'emerald' }) => {
  const colors = {
    emerald: { main: '#10b981', light: '#34d399', glow: '#6ee7b7' },
    gold: { main: '#d4af37', light: '#fbbf24', glow: '#fde68a' },
    amethyst: { main: '#7c3aed', light: '#a78bfa', glow: '#c4b5fd' },
    teal: { main: '#0d9488', light: '#14b8a6', glow: '#5eead4' }
  };
  const c = colors[color];

  return (
    <div className="absolute pointer-events-none crystal-pulse" style={{ ...position, width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`crystal-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={c.light} />
            <stop offset="50%" stopColor={c.main} />
            <stop offset="100%" stopColor={c.main} />
          </linearGradient>
          <filter id={`glow-${color}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g filter={`url(#glow-${color})`}>
          <polygon points="50,10 30,40 30,90 50,110 70,90 70,40" fill={`url(#crystal-${color})`} opacity="0.85" />
          <polygon points="50,10 30,40 50,50 70,40" fill={c.glow} opacity="0.4" />
          <polygon points="50,50 30,40 30,90 50,110" fill={c.main} opacity="0.6" />
        </g>
      </svg>
    </div>
  );
};

// Mystical Moonflower (replaces flat flower)
export const Moonflower = ({ size = 70, position = {}, delay = 0, color = 'emerald' }) => {
  const colors = {
    emerald: { petal: '#10b981', center: '#d4af37', glow: '#6ee7b7' },
    gold: { petal: '#d4af37', center: '#fef3c7', glow: '#fde047' },
    teal: { petal: '#0d9488', center: '#fbbf24', glow: '#5eead4' },
    deep: { petal: '#064e3b', center: '#d4af37', glow: '#10b981' }
  };
  const c = colors[color];

  return (
    <div
      className="moonflower-bloom absolute pointer-events-none"
      style={{ ...position, animationDelay: `${delay}s`, width: size, height: size }}
    >
      <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id={`moon-${color}-${delay}`}>
            <stop offset="0%" stopColor={c.glow} stopOpacity="1" />
            <stop offset="60%" stopColor={c.petal} stopOpacity="0.9" />
            <stop offset="100%" stopColor={c.petal} stopOpacity="0.5" />
          </radialGradient>
        </defs>
        {/* 6 elongated petals for moonflower look */}
        {[0, 60, 120, 180, 240, 300].map((angle) => (
          <ellipse
            key={angle}
            cx="50"
            cy="30"
            rx="8"
            ry="22"
            fill={`url(#moon-${color}-${delay})`}
            transform={`rotate(${angle} 50 50)`}
          />
        ))}
        {/* Inner glow */}
        <circle cx="50" cy="50" r="10" fill={c.center} opacity="0.9" />
        <circle cx="50" cy="50" r="5" fill={c.glow} />
      </svg>
    </div>
  );
};

// Hanging vine with leaves
export const Vine = ({ position = {}, length = 200, side = 'left' }) => {
  return (
    <div className="absolute pointer-events-none vine-sway" style={{ ...position, height: length, transformOrigin: 'top center' }}>
      <svg width="60" height={length} viewBox={`0 0 60 ${length}`} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`vine-grad-${side}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#064e3b" />
            <stop offset="100%" stopColor="#065f46" />
          </linearGradient>
        </defs>
        {/* Curved stem */}
        <path
          d={`M 30 0 Q ${side === 'left' ? 50 : 10} ${length / 3} 30 ${length / 2} Q ${side === 'left' ? 10 : 50} ${length * 0.7} 30 ${length}`}
          stroke="url(#vine-grad-left)"
          strokeWidth="2.5"
          fill="none"
        />
        {/* Leaves */}
        {[...Array(Math.floor(length / 40))].map((_, i) => {
          const y = 20 + i * 40;
          const isLeft = i % 2 === 0;
          return (
            <ellipse
              key={i}
              cx={isLeft ? 18 : 42}
              cy={y}
              rx="10"
              ry="5"
              fill="#10b981"
              opacity="0.7"
              transform={`rotate(${isLeft ? -30 : 30} ${isLeft ? 18 : 42} ${y})`}
            />
          );
        })}
      </svg>
    </div>
  );
};

// Mystical Star/Sparkle - more elegant
export const FairySparkle = ({ position = {}, delay = 0, size = 24 }) => {
  return (
    <div
      className="fairy-sparkle absolute pointer-events-none"
      style={{ ...position, animationDelay: `${delay}s`, width: size, height: size }}
    >
      <svg width={size} height={size} viewBox="0 0 30 30">
        <defs>
          <radialGradient id={`spark-${delay}`}>
            <stop offset="0%" stopColor="#fef3c7" />
            <stop offset="50%" stopColor="#d4af37" />
            <stop offset="100%" stopColor="#92400e" stopOpacity="0" />
          </radialGradient>
        </defs>
        <path d="M15 0 L17 13 L30 15 L17 17 L15 30 L13 17 L0 15 L13 13 Z" fill={`url(#spark-${delay})`} />
        <circle cx="15" cy="15" r="2" fill="#fef3c7" />
      </svg>
    </div>
  );
};

// Floating Lantern - mystical
export const FloatingLantern = ({ position = {}, delay = 0 }) => {
  return (
    <div
      className="floating-lantern absolute pointer-events-none"
      style={{ ...position, animationDelay: `${delay}s` }}
    >
      <svg width="40" height="60" viewBox="0 0 40 60" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id={`lantern-${delay}`}>
            <stop offset="0%" stopColor="#fef3c7" />
            <stop offset="60%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#92400e" />
          </radialGradient>
          <filter id={`lantern-glow-${delay}`}>
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        {/* Glow */}
        <ellipse cx="20" cy="30" rx="18" ry="22" fill={`url(#lantern-${delay})`} opacity="0.4" filter={`url(#lantern-glow-${delay})`} />
        {/* Lantern body */}
        <path d="M 8 20 L 8 40 Q 8 45 12 45 L 28 45 Q 32 45 32 40 L 32 20 Q 32 15 28 15 L 12 15 Q 8 15 8 20 Z" fill={`url(#lantern-${delay})`} />
        {/* Top */}
        <rect x="14" y="10" width="12" height="6" fill="#451a03" rx="1" />
        <rect x="18" y="5" width="4" height="6" fill="#451a03" />
        {/* Bottom */}
        <rect x="14" y="45" width="12" height="4" fill="#451a03" rx="1" />
      </svg>
    </div>
  );
};

// Mystical Forest Canopy - combines all elements
export const MysticalForest = ({ density = 'medium' }) => {
  const densityConfig = {
    light: { fireflies: 30, moonflowers: 5, moths: 1, crystals: 2, sparkles: 6, ferns: 4, vines: 2, lanterns: 1 },
    medium: { fireflies: 50, moonflowers: 8, moths: 2, crystals: 3, sparkles: 10, ferns: 6, vines: 3, lanterns: 2 },
    heavy: { fireflies: 80, moonflowers: 12, moths: 3, crystals: 5, sparkles: 15, ferns: 8, vines: 4, lanterns: 3 }
  };
  const config = densityConfig[density] || densityConfig.medium;

  const moonflowerPositions = [
    { top: '8%', left: '5%', color: 'gold', size: 55, delay: 0 },
    { top: '15%', right: '7%', color: 'emerald', size: 65, delay: 0.5 },
    { bottom: '12%', left: '8%', color: 'teal', size: 70, delay: 1 },
    { bottom: '18%', right: '10%', color: 'gold', size: 60, delay: 1.5 },
    { top: '45%', left: '3%', color: 'emerald', size: 50, delay: 2 },
    { bottom: '40%', right: '4%', color: 'deep', size: 55, delay: 2.5 },
    { top: '70%', left: '15%', color: 'gold', size: 45, delay: 3 },
    { top: '25%', right: '20%', color: 'emerald', size: 50, delay: 3.5 },
    { bottom: '60%', left: '22%', color: 'teal', size: 48, delay: 4 },
    { top: '60%', right: '22%', color: 'deep', size: 52, delay: 4.5 },
    { top: '85%', left: '45%', color: 'gold', size: 40, delay: 5 },
    { top: '5%', right: '35%', color: 'emerald', size: 45, delay: 5.5 }
  ];

  const sparklePositions = [
    { top: '15%', left: '20%' }, { top: '30%', right: '25%' }, { bottom: '20%', left: '30%' },
    { top: '50%', left: '45%' }, { bottom: '40%', right: '40%' }, { top: '70%', left: '60%' },
    { top: '10%', right: '40%' }, { bottom: '15%', left: '55%' }, { top: '85%', right: '15%' },
    { top: '40%', left: '70%' }, { top: '60%', left: '35%' }, { bottom: '50%', right: '30%' },
    { top: '20%', left: '55%' }, { top: '75%', left: '25%' }, { bottom: '30%', right: '55%' }
  ];

  const fernPositions = [
    { top: '5%', right: '2%', rotation: 15, size: 140, opacity: 0.4 },
    { bottom: '5%', left: '2%', rotation: -15, size: 150, opacity: 0.4 },
    { top: '40%', left: '-2%', rotation: 90, size: 120, opacity: 0.3 },
    { bottom: '40%', right: '-2%', rotation: -90, size: 130, opacity: 0.3 },
    { top: '15%', left: '40%', rotation: 0, size: 90, opacity: 0.2 },
    { bottom: '15%', left: '50%', rotation: 180, size: 100, opacity: 0.25 },
    { top: '70%', right: '35%', rotation: 45, size: 110, opacity: 0.3 },
    { top: '30%', right: '50%', rotation: -45, size: 95, opacity: 0.25 }
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Deep forest atmosphere - mystical orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-600/20 rounded-full mix-blend-screen filter blur-3xl animate-blob"></div>
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-amber-500/15 rounded-full mix-blend-screen filter blur-3xl animate-blob" style={{ animationDelay: '2s' }}></div>
      <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-teal-600/20 rounded-full mix-blend-screen filter blur-3xl animate-blob" style={{ animationDelay: '4s' }}></div>

      {/* Hanging vines from top */}
      {config.vines >= 1 && <Vine position={{ top: 0, left: '10%' }} length={250} side="left" />}
      {config.vines >= 2 && <Vine position={{ top: 0, right: '12%' }} length={280} side="right" />}
      {config.vines >= 3 && <Vine position={{ top: 0, left: '55%' }} length={200} side="left" />}
      {config.vines >= 4 && <Vine position={{ top: 0, right: '40%' }} length={220} side="right" />}

      {/* Ferns */}
      {fernPositions.slice(0, config.ferns).map((pos, i) => (
        <Fern key={`fern-${i}`} position={{ top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right }} rotation={pos.rotation} size={pos.size} opacity={pos.opacity} />
      ))}

      {/* Moonflowers */}
      {moonflowerPositions.slice(0, config.moonflowers).map((pos, i) => (
        <Moonflower
          key={`flower-${i}`}
          color={pos.color}
          size={pos.size}
          delay={pos.delay}
          position={{ top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right }}
        />
      ))}

      {/* Glowing Crystals */}
      {config.crystals >= 1 && <GlowingCrystal position={{ bottom: '8%', left: '15%' }} size={70} color="emerald" />}
      {config.crystals >= 2 && <GlowingCrystal position={{ bottom: '12%', right: '18%' }} size={55} color="gold" />}
      {config.crystals >= 3 && <GlowingCrystal position={{ top: '50%', right: '8%' }} size={50} color="teal" />}
      {config.crystals >= 4 && <GlowingCrystal position={{ top: '20%', left: '38%' }} size={45} color="amethyst" />}
      {config.crystals >= 5 && <GlowingCrystal position={{ bottom: '35%', left: '40%' }} size={48} color="emerald" />}

      {/* Floating Lanterns */}
      {config.lanterns >= 1 && <FloatingLantern position={{ top: '15%', left: '30%' }} delay={0} />}
      {config.lanterns >= 2 && <FloatingLantern position={{ bottom: '25%', right: '35%' }} delay={2} />}
      {config.lanterns >= 3 && <FloatingLantern position={{ top: '60%', left: '70%' }} delay={4} />}

      {/* Fairy sparkles */}
      {sparklePositions.slice(0, config.sparkles).map((pos, i) => (
        <FairySparkle key={`sparkle-${i}`} position={pos} delay={i * 0.3} size={20 + (i % 3) * 6} />
      ))}

      {/* Moths */}
      {[...Array(config.moths)].map((_, i) => (
        <Moth key={`moth-${i}`} delay={i * 4} top={`${20 + i * 25}%`} />
      ))}

      {/* Fireflies overlay */}
      <Fireflies count={config.fireflies} />
    </div>
  );
};

// Backwards compatibility aliases
export const MysticalGarden = MysticalForest;
export const Butterfly = Moth;
export const Flower = Moonflower;
export const Mushroom = GlowingCrystal;
export const Leaf = Fern;

export const FloatingFairy = ({ delay = 0 }) => <FairySparkle delay={delay} />;
export const MysticalOrb = ({ size = 'md', color = 'emerald' }) => {
  const sizes = { sm: 'w-32 h-32', md: 'w-48 h-48', lg: 'w-64 h-64' };
  const colors = { purple: 'bg-emerald-600/30', pink: 'bg-amber-500/30', lavender: 'bg-teal-600/30', emerald: 'bg-emerald-600/30' };
  return <div className={`${sizes[size]} ${colors[color] || colors.emerald} rounded-full mix-blend-screen filter blur-3xl opacity-40 animate-pulse`} />;
};
export const StarField = () => <Fireflies count={20} />;
